import { useEffect, useMemo } from "react";
import * as THREE from "three";
import type { BuildInput } from "../../engines/types";
import {
	type ClearanceEnvelopeBox,
	resolveClearanceEnvelope,
} from "./heaterClearanceEnvelope";

// 3D materials can't read CSS custom properties — these mirror
// src/styles/tokens.css (--forest / builder dark-mode --error).
const COLOR_OK = "#5a6b50";
const COLOR_VIOLATION = "#d84848";

interface HeaterClearanceOverlayProps {
	buildInput: BuildInput;
	visible: boolean;
}

/**
 * Translucent heater safety-clearance envelope.
 * Green when the layout respects the BBR/manufacturer minimum distances,
 * red when a bench, wall, partition or the ceiling intrudes.
 *
 * Static annotation: recomputes via useMemo on buildInput change, renders
 * no per-frame work, and unmounts its subtree entirely when toggled off —
 * so the demand-mode frameloop stays idle.
 *
 * The envelope is resolved through `resolveHeaterClearanceLayout`, which is the
 * same call the enrichment chain makes. Calling `deriveHeaterClearanceLayout`
 * directly draws the zone around the aggregat's GENERATED position — which
 * stopped being where the model puts it once the renderer began following
 * `layout.heater` (issue 131). A safety envelope drawn around empty floor is
 * worse than none, and the verdict text being correct hides it.
 */
export function HeaterClearanceOverlay({
	buildInput,
	visible,
}: HeaterClearanceOverlayProps) {
	const envelope = useMemo<ClearanceEnvelopeBox | null>(
		() => (visible ? resolveClearanceEnvelope(buildInput) : null),
		[buildInput, visible],
	);

	const [width, height, depth] = envelope?.sizeM ?? [0, 0, 0];

	const edgesGeometry = useMemo(() => {
		if (width <= 0 || height <= 0 || depth <= 0) return null;
		const box = new THREE.BoxGeometry(width, height, depth);
		const edges = new THREE.EdgesGeometry(box);
		box.dispose();
		return edges;
	}, [width, height, depth]);

	useEffect(() => () => edgesGeometry?.dispose(), [edgesGeometry]);

	if (!visible || !envelope || !edgesGeometry) {
		return null;
	}

	const color = envelope.status === "ok" ? COLOR_OK : COLOR_VIOLATION;

	return (
		<group name="overlay/heater-clearance" position={envelope.centerM}>
			<mesh name="overlay/heater-clearance-volume" renderOrder={10}>
				<boxGeometry args={[width, height, depth]} />
				<meshBasicMaterial
					color={color}
					transparent
					opacity={0.18}
					depthWrite={false}
					side={THREE.DoubleSide}
				/>
			</mesh>
			<lineSegments
				name="overlay/heater-clearance-edges"
				geometry={edgesGeometry}
				renderOrder={11}
				// Geometry lifecycle is owned by the useMemo/useEffect pair
				// above — R3F must not dispose it on hide/show toggles.
				dispose={null}
			>
				<lineBasicMaterial color={color} transparent opacity={0.85} />
			</lineSegments>
		</group>
	);
}
